import { OnInit, OnDestroy, Component } from '@angular/core';
import { Observable ,  Subscription ,  BehaviorSubject } from 'rxjs';
import { ErrorProducer } from './error-producer';
import { FieldError } from './field-error';

@Component({ template: '' })
export class BaseComponent implements OnInit, OnDestroy, ErrorProducer {
    constructor(
    ) {
        let proto = Object.getPrototypeOf(this);
        let properties = Object.getOwnPropertyNames(proto);

        let usesLifecycleEvents = 
            properties.indexOf('ngOnInit') >= 0
            || properties.indexOf('ngOnDestroy') >= 0
        ;

        if (usesLifecycleEvents) {
            throw new Error(`Component ${this.constructor.name} which inherits BaseComponent is not allowed to declare the ngOnInit and/or ngOnDestroy lifecycle events. Instead implement init() and/or destroy()`);
        }
    }

    private _onInitHappened = false;
    private _initArgs = [];
    private _initialized = false;
    private _destroyed = false;

    public errorChanged : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public _error : boolean = false;
    public errorMessage : string = null;
    public fieldErrors : FieldError[];

    public get error() { return this._error; }
    public set error(value : boolean) { 
        this._error = value; 
        this.errorChanged.next(value);
    }

    public hideError() {
        this.errorMessage = '';
        this.fieldErrors = [];
        this.error = false;
    }
    
    public removeFieldError(field : string) {
        if (!this.fieldErrors)
            this.fieldErrors = [];
        
        this.fieldErrors = this.fieldErrors.filter(x => x.field != field);
        this.error = this.fieldErrors.length > 0;
    }

    public addFieldError(field : string, message? : string) {
        if (!this.fieldErrors)
            this.fieldErrors = [];

        this.removeFieldError(field);
        this.fieldErrors.push({
            field, message
        });
        this.error = true;
    }

    public showError(message : string, fieldErrors : FieldError[] = []) {

        if (!message)
            message = 'An unknown error occurred. Please try again and if this issue persists please contact support. (ERR_NO_MSG)'
        console.error('Error was shown');
        console.error('Message: '+message);

        console.dir(fieldErrors);

        this.errorMessage = message;
        this.fieldErrors = fieldErrors;
        this.error = true;
    }

    set initArguments(args : any[]) {

        this._initArgs = args;
    }

    ngOnInit() {
        this.fireInit();
    }

    /**
     * Fires the component init() method. Can be overridden by subclasses to wrap their own subclasses init() cycle.
     */
    fireInit() {
        this._initialized = true;
        this.init.apply(this, this._initArgs);
    }

    /**
     * Subscribe to the given observable for the lifetime of this component (ie unsubscribed automatically when component is destroyed).
     */
    subscribe<T>(observable : Observable<T>, action : (value : T) => void): void {
        this._subscriptions.push(observable.subscribe(action));
    }

    private _subscriptions : Subscription[] = [];

    ngOnDestroy() {
        if (!this._initialized || this._destroyed)
            return;
        
        this._destroyed = true;
        this._subscriptions.forEach(sub => sub.unsubscribe());
        this._subscriptions = [];
        this.destroy();
    }

    /**
     * Override this method to initialize the component.
     */
    init(...args) { }

    /**
     * Override this method to specify actions to happen when the component is destroyed.
     */
    destroy() { }
}